/* eslint-disable @typescript-eslint/member-ordering */
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { SALES, VerticalType } from '@rma/generic/domain/types';
import { MAP_AREA_TRANSLATION_KEY, MapMarkerType, MapRequest } from '@rma/generic/maps/domain';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { AutoCompleteResult, AutoSearchApiService, LocationsAutoSearchResult } from '@rma/public/generic/api/auto-search';
import { LocationType, PagedList } from '@rma/public/generic/domain/types';
import { filter, map, Observable, switchMap } from 'rxjs';

export interface MapLocation {
  mapType: MapMarkerType;
  verticalType: VerticalType;
  baseRequest?: MapRequest;
  initialLocationId?: number;
  initialLocationType?: LocationType;
  initialLocationText?: string;
  searchTerm: string;
  skip: number;
  results: PagedList<AutoCompleteResult>;
  locationId?: number;
  locationType?: LocationType;
  locationText?: string;
  addressCode?: string;
  error?: unknown;
}

@Injectable()
export class MapLocationStore extends ComponentStore<MapLocation> {
  private readonly autoSearchApiService = inject(AutoSearchApiService);
  private readonly translocoService = inject(TranslocoService);

  private readonly mapType$ = this.select(({ mapType }) => mapType);
  private readonly verticalType$ = this.select(({ verticalType }) => verticalType);
  private readonly locationId$ = this.select(({ locationId }) => locationId);
  private readonly locationType$ = this.select(({ locationType }) => locationType);
  private readonly locationText$ = this.select(({ locationText }) => locationText);
  private readonly initialLocationText$ = this.select(({ initialLocationText }) => initialLocationText);
  private readonly addressCode$ = this.select(({ addressCode }) => addressCode);

  readonly name$ = this.select(this.initialLocationText$, this.locationText$, this.locationId$, (initialText, text, id) =>
    id ? (text ?? initialText) : this.translocoService.translate(MAP_AREA_TRANSLATION_KEY, {}, 'maps'),
  ).pipe(nonNullable());
  readonly searchTerm$ = this.select(({ searchTerm }) => searchTerm).pipe(nonNullable());
  readonly skip$ = this.select(({ skip }) => skip ?? 0);

  private readonly debouncedBaseRequest$ = this.select(
    {
      mapType: this.mapType$,
      verticalType: this.verticalType$,
      locationId: this.locationId$,
      locationType: this.locationType$,
      addressCode: this.addressCode$,
    },
    { debounce: true },
  ).pipe(filter(({ locationId }) => !!locationId));

  readonly baseRequest$ = this.debouncedBaseRequest$.pipe(
    map(({ mapType, verticalType, locationId, locationType, addressCode }) =>
      this.getBaseRequest(mapType, verticalType, locationId, locationType, addressCode),
    ),
  );

  private readonly locationSearchRequest$ = this.select({ skip: this.skip$, searchTerm: this.searchTerm$ }, { debounce: true });

  readonly locationSearchResults$ = this.select(({ results }) => results);

  constructor() {
    super({
      mapType: 'Review',
      verticalType: SALES,
      searchTerm: '',
      skip: 0,
      results: { skip: 0, take: 0, total: 0, results: [] },
    });

    this.locationSearchEffect(this.locationSearchRequest$);
  }

  public readonly locationSearchEffect = this.effect((request$: Observable<{ skip: number; searchTerm: string }>) =>
    request$.pipe(
      filter(({ searchTerm }) => !!searchTerm),
      switchMap(({ skip, searchTerm }) =>
        this.autoSearchApiService
          .getLocations({
            skip,
            take: 10,
            searchTerm,
          })
          .pipe(
            tapResponse(
              (results) => this.setLocationSearchResults({ skip, results }),
              (error) => {
                console.error(error);
                this.patchState({ error });
              },
            ),
          ),
      ),
    ),
  );

  public setMapType(mapType: MapMarkerType) {
    this.patchState({ mapType });
  }

  public setVerticalType(verticalType: VerticalType) {
    this.patchState({ verticalType });
  }

  public setInitialLocation(initialLocationId: number, initialLocationType: LocationType, initialLocationText: string) {
    this.patchState({
      initialLocationId,
      locationId: initialLocationId,
      initialLocationType,
      locationType: initialLocationType,
      initialLocationText,
      locationText: initialLocationText,
    });
  }

  public setLocation(locationId?: number, locationType?: LocationType, locationText?: string) {
    this.patchState({ locationId, locationType, locationText });
  }

  public setPrimaryAddressCode(addressCode: string) {
    this.patchState({ addressCode });
  }

  public resetSearch() {
    this.patchState((state) => ({
      locationId: state.initialLocationId,
      locationType: state.initialLocationType,
    }));
  }

  public locationSearch(searchTerm: string) {
    this.patchState({
      searchTerm,
      skip: 0,
      error: undefined,
    });
  }

  public locationViewMore() {
    this.patchState((state) => ({
      skip: (state.skip ?? 0) + 10,
    }));
  }

  private setLocationSearchResults(response: { skip: number; results: PagedList<LocationsAutoSearchResult> }) {
    this.patchState((state) => ({
      skip: response.skip,
      results:
        response.skip === 0
          ? {
              ...response.results,
              results: response.results.results.map(this.mapAutoSearchToAutoCompleteResult),
            }
          : {
              ...response.results,
              results: [...state.results.results, ...response.results.results.map(this.mapAutoSearchToAutoCompleteResult)],
            },
    }));
  }

  private mapAutoSearchToAutoCompleteResult(autoSearch: LocationsAutoSearchResult): AutoCompleteResult {
    const { id, name, rawName, url, autoSearchSubType } = autoSearch;
    const result: AutoCompleteResult = {
      id,
      name,
      rawName,
      url,
      autoSearchSubType,
    };

    return result;
  }

  private getBaseRequest(
    mapType: MapMarkerType,
    verticalType: VerticalType,
    locationId?: number,
    locationType?: LocationType,
    addressCode?: string,
    markerCount = 50,
  ): MapRequest {
    const request: MapRequest = {
      markerType: mapType,
      verticalType,
      take: markerCount,
      locationId,
      locationType,
      excludeAddressCode: addressCode,
      from: new Date(2014, 1, 1),
    };

    return request;
  }
}
